import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { X } from "lucide-react";
import { api } from "../../lib/api";
import { EventStatusPill } from "../../components/admin/Pills";
import { usePageMeta } from "../../components/Layout";

const STATUSES = ["pending", "processed", "duplicate", "failed", "failed_permanent"];

export default function MetaEvents() {
    usePageMeta({ title: "Meta events · Tools | Studio G4 Admin" });
    const [params, setParams] = useSearchParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [retrying, setRetrying] = useState(false);

    const selected = useMemo(() => params.getAll("status"), [params]);
    const page = parseInt(params.get("page") || "1", 10);

    const fetchList = () => {
        setLoading(true);
        const query = params.toString();
        const url = `/admin/meta-events${query ? `?${query}&page_size=25` : "?page_size=25"}`;
        api.get(url).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0 })).finally(() => setLoading(false));
    };

    useEffect(fetchList, [params]);

    const toggle = (s) => {
        const p = new URLSearchParams(params);
        const cur = p.getAll("status");
        p.delete("status");
        (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]).forEach((x) => p.append("status", x));
        p.delete("page");
        setParams(p, { replace: true });
    };

    const open = async (id) => {
        setSelectedId(id);
        setDetail(null);
        try {
            const { data } = await api.get(`/admin/meta-events/${id}`);
            setDetail(data);
        } catch { toast.error("Could not load event"); }
    };

    const retry = async () => {
        if (!detail) return;
        setRetrying(true);
        try {
            await api.post(`/admin/meta-events/${detail.id}/retry`);
            toast.success("Retry queued");
            setSelectedId(null);
            fetchList();
        } catch { toast.error("Retry failed"); } finally { setRetrying(false); }
    };

    const totalPages = Math.max(1, Math.ceil((data?.total || 0) / 25));

    return (
        <div className="space-y-6" data-testid="meta-events-page">
            <div>
                <h1 className="font-manrope font-bold text-charcoal text-[28px] md:text-[34px]">Meta events</h1>
                <p className="font-inter text-charcoal/60 text-[13.5px] mt-1">Webhook deliveries from Meta Lead Ads.</p>
            </div>

            <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => {
                    const active = selected.includes(s);
                    return (
                        <button
                            key={s}
                            onClick={() => toggle(s)}
                            className={"px-3 py-1.5 rounded-md text-[12.5px] font-manrope font-semibold border transition-colors " + (active ? "border-terracotta bg-terracotta text-offwhite" : "border-charcoal/20 text-charcoal hover:border-charcoal/40")}
                            data-testid={`event-filter-${s}`}
                        >
                            {s}
                        </button>
                    );
                })}
                {selected.length > 0 && (
                    <button onClick={() => setParams(new URLSearchParams(), { replace: true })} className="ml-auto text-terracotta font-manrope font-semibold text-[12.5px] hover:underline">Clear</button>
                )}
            </div>

            <div className="bg-white/60 border border-charcoal/10 rounded-md overflow-x-auto">
                {loading && <div className="p-6 text-center text-charcoal/50 font-inter text-[13px]">Loading…</div>}
                {!loading && (data?.items || []).length === 0 && (
                    <div className="p-10 text-center font-manrope font-semibold text-charcoal">No Meta events yet.</div>
                )}
                {!loading && (data?.items || []).length > 0 && (
                    <table className="w-full text-[13.5px]">
                        <thead>
                            <tr className="text-left text-charcoal/50 font-manrope font-semibold text-[11px] uppercase tracking-wider">
                                <th className="px-4 py-3">Received</th>
                                <th className="px-4 py-3">Lead ID</th>
                                <th className="px-4 py-3">Form ID</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Att.</th>
                                <th className="px-4 py-3">Last error</th>
                                <th className="px-4 py-3">Linked</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map((e) => (
                                <tr key={e.id} onClick={() => open(e.id)} className="cursor-pointer border-t border-charcoal/10 hover:bg-charcoal/[0.03]" data-testid={`event-row-${e.id}`}>
                                    <td className="px-4 py-3 text-charcoal/70 whitespace-nowrap">{e.received_at ? format(parseISO(e.received_at), "d MMM · HH:mm") : ""}</td>
                                    <td className="px-4 py-3 font-mono text-[12px] text-charcoal/70" title={e.leadgen_id}>{(e.leadgen_id || "").slice(0, 14)}…</td>
                                    <td className="px-4 py-3 font-mono text-[12px] text-charcoal/70">{(e.form_id || "—").slice(0, 12)}</td>
                                    <td className="px-4 py-3"><EventStatusPill value={e.status} /></td>
                                    <td className="px-4 py-3 text-center">{e.attempts}</td>
                                    <td className="px-4 py-3 text-charcoal/70 max-w-[240px] truncate" title={e.last_error || ""}>{e.last_error || ""}</td>
                                    <td className="px-4 py-3">{e.enquiry_id && <Link to={`/admin/projects/${e.enquiry_id}`} onClick={(ev) => ev.stopPropagation()} className="text-terracotta hover:underline">View →</Link>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {totalPages > 1 && (
                <div className="flex justify-between text-[13px] font-inter text-charcoal/70">
                    <div>Page {page} of {totalPages}</div>
                    <div className="flex gap-2">
                        <button disabled={page <= 1} onClick={() => { const p = new URLSearchParams(params); p.set("page", String(page - 1)); setParams(p); }} className="px-3 py-1.5 border border-charcoal/20 rounded font-manrope font-semibold disabled:opacity-40">← Prev</button>
                        <button disabled={page >= totalPages} onClick={() => { const p = new URLSearchParams(params); p.set("page", String(page + 1)); setParams(p); }} className="px-3 py-1.5 border border-charcoal/20 rounded font-manrope font-semibold disabled:opacity-40">Next →</button>
                    </div>
                </div>
            )}

            {selectedId && (
                <div className="fixed inset-0 z-50 bg-charcoal/40 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setSelectedId(null)}>
                    <div className="bg-white rounded-md max-w-2xl w-full max-h-[85vh] overflow-y-auto" data-testid="event-detail-modal">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-charcoal/10">
                            <h3 className="font-manrope font-bold text-charcoal text-[18px]">Event detail</h3>
                            <button onClick={() => setSelectedId(null)} className="text-charcoal/60 hover:text-charcoal p-1"><X size={18} /></button>
                        </div>
                        <div className="p-6 space-y-4 text-[13.5px] font-inter">
                            {!detail && <div className="text-charcoal/50">Loading…</div>}
                            {detail && (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {["received_at", "processed_at", "next_retry_at", "leadgen_id", "form_id", "page_id", "signature_valid", "attempts", "status", "enquiry_id"].map((k) => (
                                            <div key={k}>
                                                <div className="text-charcoal/50 text-[11.5px] font-manrope uppercase tracking-wider">{k}</div>
                                                <div className="break-all">{k === "status" ? <EventStatusPill value={detail[k]} /> : (detail[k] === null || detail[k] === undefined ? "—" : String(detail[k]))}</div>
                                            </div>
                                        ))}
                                    </div>
                                    {detail.last_error && (
                                        <div>
                                            <div className="text-charcoal/50 text-[11.5px] font-manrope uppercase tracking-wider mb-1">Last error</div>
                                            <div className="bg-red-50 border border-red-200 rounded p-3 text-red-700 text-[12.5px] font-mono">{detail.last_error}</div>
                                        </div>
                                    )}
                                    <details>
                                        <summary className="cursor-pointer font-manrope font-semibold text-charcoal">Raw payload</summary>
                                        <pre className="mt-2 bg-charcoal/5 rounded p-3 text-[11.5px] overflow-x-auto">{JSON.stringify(detail.raw_payload, null, 2)}</pre>
                                    </details>
                                </>
                            )}
                        </div>
                        <div className="flex justify-end gap-2 px-6 py-4 border-t border-charcoal/10">
                            {detail && (detail.status === "failed" || detail.status === "failed_permanent") && (
                                <button onClick={retry} disabled={retrying} className="px-4 py-2 rounded-md bg-terracotta text-offwhite font-manrope font-semibold text-[13.5px] hover:bg-terracotta-600 disabled:opacity-70" data-testid="event-retry">
                                    {retrying ? "Queuing…" : "Retry"}
                                </button>
                            )}
                            <button onClick={() => setSelectedId(null)} className="px-4 py-2 text-charcoal/70 hover:text-charcoal font-manrope font-semibold text-[13.5px]">Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
